import React, { useEffect, useRef, useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import axios from 'axios'
import Toastify from 'toastify-js'
import { pushEvent, EVENTS } from '../../utils/dataLayer'
import { logGuestVisit } from '../../utils/guestVisitLog'
import { markWeeklyEntry } from '../../utils/entryFlow'
import { captureGoogleClick, reportGoogleClickPhone } from '../../utils/googleClickId'
import '../Weekly/weekly.css'

const authHeaders = () => ({
  Authorization: localStorage.getItem('dastaan__token'),
  Accept: 'application/json',
  'Content-Type': 'application/json',
});

// Same check ProtectedRoute uses for an active subscription.
const hasActivePlan = () => {
  const planId = localStorage.getItem('dastaan__plan_id');
  const validPlanId = planId && planId !== 'null' && planId !== 'undefined' && planId !== '';
  return Boolean(validPlanId) || localStorage.getItem('dastaan__plan_active') === 'true';
};

// Pick the weekly plan from /plans (7-day plan, or one named "Weekly").
const findWeeklyPlan = (plans = []) =>
  plans.find((p) => Number(p.plan_days) === 7) ||
  plans.find((p) => /week/i.test(p.plan_name || ''));

const isTruthyFlag = (v) => v === true || v === 1 || ['1', 'true', 'active', 'yes'].includes(String(v).toLowerCase());

// /payu-payment-status: PlansV2 treats a non-null exp_date as paid. Also accept
// the same fields nested under data, or an explicit success status.
// An explicit failure/cancel status ends the check early.
const readPaidStatus = (body = {}) => {
  const d = body.data && typeof body.data === 'object' ? body.data : {};
  const status = String(body.status || d.status || body.payment_status || d.payment_status || '').toLowerCase();
  const paid =
    body.exp_date != null || d.exp_date != null ||
    ['success', 'captured', 'paid'].includes(status);
  return {
    paid,
    failed: !paid && ['failure', 'failed', 'cancelled', 'canceled', 'usercancelled', 'user_cancelled', 'bounced', 'dropped'].includes(status),
    planId: body.plan_id || d.plan_id,
    planAmnt: body.plan_amnt || body.plan_amount || d.plan_amnt || d.plan_amount,
  };
};

const PAYMENT_CHECK_MS = 120000;

// Countdown shown while the payment is being confirmed.
const PaymentTimer = ({ deadline }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const left = Math.max(0, Math.ceil((deadline - now) / 1000));
  const mm = Math.floor(left / 60);
  const ss = String(left % 60).padStart(2, '0');
  const pct = Math.min(100, (left * 1000 / PAYMENT_CHECK_MS) * 100);
  return (
    <div className='weekly-page__timer'>
      <span className='weekly-page__timer-text'>{mm}:{ss}</span>
      <div className='weekly-page__timer-bar'><span style={{ width: `${pct}%` }} /></div>
    </div>
  );
};

const toPlanItem = (plan) => ({
  item_id: String(plan.id),
  item_name: plan.plan_name,
  price: plan.plan_price - plan.discount,
  discount: plan.discount,
  item_category: 'subscription',
});

const showToast = (msg) => {
  Toastify({
    text: msg,
    duration: 3000,
    close: true,
    gravity: 'bottom',
    position: 'center',
    stopOnFocus: true,
    style: { background: 'linear-gradient(to right, #000000ff, #000000ff)' },
  }).showToast();
};

const errorMessage = (error) => {
  const status = error?.response?.status;
  if (status >= 500) return 'Server error. Please try again in a moment.';
  if (status === 0 || !status) return 'No internet connection. Please check your network.';
  return error?.response?.data?.message || 'Something went wrong. Please try again.';
};

function WeeklyLogin() {
  const navigate = useNavigate();
  const isLoggedIn = localStorage.getItem('dastaan__user_id') > 0;
  const alreadySubscribed = isLoggedIn && hasActivePlan();

  // phone -> pay. A logged-in user without a plan starts at "pay".
  const [step, setStep] = useState(isLoggedIn ? 'pay' : 'phone');
  const [phone, setPhone] = useState('');
  const [plan, setPlan] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pages, setPages] = useState({ terms: '', privacy: '' });
  // Returning from PayU with a pending transaction: confirm it here.
  // Polling finished without confirmation: offer "Check again" / "Pay again".
  const [unconfirmed, setUnconfirmed] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(
    () => isLoggedIn && Boolean(localStorage.getItem('dastaan__pending_txnid'))
  );
  const [checkDeadline, setCheckDeadline] = useState(null);
  const loggingInRef = useRef(false);
  const pollingRef = useRef(false);
  const phoneRef = useRef(null);
  const pageRef = useRef(null);
  // Visible area above the on-screen keyboard (visualViewport), so the CTA stays reachable.
  const [viewport, setViewport] = useState(null);
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const open = window.innerHeight - vv.height > 150;
      setViewport({ height: vv.height, top: vv.offsetTop });
      setKeyboardOpen(open);
      if (open) {
        requestAnimationFrame(() => {
          pageRef.current?.querySelector('.weekly-page__cta')?.scrollIntoView({ block: 'end', behavior: 'smooth' });
        });
      }
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  useEffect(() => {
    logGuestVisit('weekly-page');
    captureGoogleClick();
    markWeeklyEntry();
    const pendingTxnid = localStorage.getItem('dastaan__pending_txnid');
    if (isLoggedIn && pendingTxnid) pollPaymentStatus(pendingTxnid);
    phoneRef.current?.focus();
    axios.get(`${process.env.REACT_APP_API_URL}/plans`, { headers: authHeaders(), timeout: 12000 })
      .then((response) => {
        const weekly = findWeeklyPlan(response.data.plans);
        if (weekly) {
          setPlan(weekly);
          pushEvent(EVENTS.VIEW_PLANS, { ecommerce: { item_list_name: 'weekly', items: [toPlanItem(weekly)] } });
        }
      })
      .catch(() => {});
    axios.get(`${process.env.REACT_APP_API_URL}/pages`, {
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    }).then((response) => {
      const next = {};
      (response.data.data || []).forEach((item) => {
        if (item.page_name === 'Terms and Condition') next.terms = `new/weekly/pages/${item.page_name}`;
        if (item.page_name === 'Privacy Policy') next.privacy = `new/weekly/pages/${item.page_name}`;
      });
      setPages((prev) => ({ ...prev, ...next }));
    }).catch(() => {});
    // Mount-only: isLoggedIn is read once from localStorage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Back from the UPI app / PayU tab (visibilitychange, focus) or restored
  // from the back-forward cache (pageshow): confirm any pending payment.
  useEffect(() => {
    const onReturn = (event) => {
      if (document.hidden) return;
      if (event.type === 'pageshow' && !event.persisted) return;
      const txnid = localStorage.getItem('dastaan__pending_txnid');
      if (txnid && localStorage.getItem('dastaan__user_id') > 0) pollPaymentStatus(txnid);
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    window.addEventListener('pageshow', onReturn);
    return () => {
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
      window.removeEventListener('pageshow', onReturn);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) setPhone(value);
  };

  // Log in directly with the phone number (no OTP), then go to payment.
  const loginWithPhone = async () => {
    if (loggingInRef.current || phone.length !== 10) return;
    loggingInRef.current = true;
    setIsSubmitting(true);
    const utmData = JSON.parse(localStorage.getItem('utmData')) || {};
    const data = {
      phone,
      utm_source: utmData.utm_source || '',
      utm_medium: utmData.utm_medium || '',
      utm_campaign: utmData.utm_campaign || '',
      click_id: utmData.click_id || '',
    };
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/login-with-phone`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      const res = response?.data?.Podcast_App?.[0];
      if (!res?.token || (res.success !== undefined && String(res.success) !== '1')) {
        const err = new Error('Login rejected');
        err.loginMessage = res?.msg;
        throw err;
      }
      reportGoogleClickPhone(phone);
      const profile = res?.user_profiles?.[0];
      const plan_id = profile?.plan_id;
      const plan_amnt = profile?.plan_amount;
      localStorage.setItem('dastaan__user_image', res.user_image);
      localStorage.setItem('dastaan__name', res?.name);
      localStorage.setItem('dastaan__user_id', res?.user_id);
      localStorage.setItem('dastaan__phone', res?.phone);
      localStorage.setItem('dastaan__token', res?.token);
      localStorage.setItem('dastaan__plan_id', plan_id);
      localStorage.setItem('dastaan__plan_amnt', plan_amnt);
      pushEvent(EVENTS.LOGIN, { method: 'phone', has_plan: plan_id != null, plan_amount: plan_amnt ?? null });
      if (plan_id != null) {
        // Already subscribed: nothing to pay for.
        showToast(res.msg);
        navigate('/');
        return;
      }
      setStep('pay');
      await startPayment();
    } catch (error) {
      pushEvent(EVENTS.LOGIN_FAILED, { method: 'phone', error_status: error?.response?.status || null });
      showToast(error.loginMessage || errorMessage(error));
      setIsSubmitting(false);
    }
    loggingInRef.current = false;
  };

  // Is the plan active according to /home? Fallback for when
  // /payu-payment-status doesn't report the payment.
  const planActiveOnServer = async () => {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/home`,
      { user_id: localStorage.getItem('dastaan__user_id') || 0 },
      { headers: authHeaders(), timeout: 15000 }
    );
    const status = response?.data?.Podcast_App?.user_plan_status;
    console.log('[weekly] /home user_plan_status:', status);
    return isTruthyFlag(status);
  };

  const goToSuccess = (txnid, { planId, planAmnt } = {}) => {
    if (planId) localStorage.setItem('dastaan__plan_id', planId);
    if (planAmnt) localStorage.setItem('dastaan__plan_amnt', planAmnt);
    localStorage.setItem('is_subscription_cancelled', 'false');
    localStorage.setItem('dastaan__plan_active', 'true');
    localStorage.removeItem('dastaan__pending_txnid');
    const amount = planAmnt || (plan ? plan.plan_price - plan.discount : '');
    const params = new URLSearchParams({ txnid, amount: amount || '' });
    window.location.href = `/new/weekly/success?${params.toString()}`;
  };

  // After coming back from PayU / the UPI app: poll every 5s for up to 3 min.
  // Paid -> /new/weekly/success; failed, or still unknown after 2 min -> "Payment failed".
  const pollPaymentStatus = (txnid) => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    setUnconfirmed(false);
    setCheckingPayment(true);
    const deadline = Date.now() + PAYMENT_CHECK_MS;
    setCheckDeadline(deadline);
    const check = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/payu-payment-status`,
          { user_id: parseInt(localStorage.getItem('dastaan__user_id')), txnid },
          { headers: authHeaders(), timeout: 15000 }
        );
        console.log('[weekly] /payu-payment-status:', response?.data);
        const result = readPaidStatus(response?.data);
        if (result.paid) {
          goToSuccess(txnid, result);
          return;
        }
        if (result.failed) {
          stopPolling();
          return;
        }
      } catch (e) {
        console.log('[weekly] /payu-payment-status error:', e?.response?.status, e?.response?.data);
      }
      try {
        if (await planActiveOnServer()) {
          goToSuccess(txnid);
          return;
        }
      } catch (e) {
        // ignore: retry until the deadline
      }
      if (Date.now() < deadline) setTimeout(check, 5000);
      else stopPolling();
    };
    const stopPolling = () => {
      pollingRef.current = false;
      setCheckingPayment(false);
      setIsSubmitting(false);
      setUnconfirmed(true);
    };
    check();
  };

  // Create a PayU intent for the weekly plan and hand off to the gateway.
  // UPI intents open the payment app and leave this tab in place, so the
  // return listeners below confirm the payment when the tab is visible again.
  // Full-page PayU redirects come back to /plans, which also confirms it.
  const startPayment = async () => {
    if (!plan) {
      showToast('Weekly plan is not available right now. Please try again.');
      setIsSubmitting(false);
      return;
    }
    setIsSubmitting(true);
    const amount = plan.plan_price - plan.discount;
    pushEvent(EVENTS.BEGIN_CHECKOUT, {
      ecommerce: { currency: 'INR', value: amount, items: [toPlanItem(plan)] },
    });
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/payu-create-intent`,
        { plan_id: plan.id, user_id: localStorage.getItem('dastaan__user_id') },
        { headers: authHeaders() }
      );
      const redirectUrl = response?.data?.data?.redirect_url;
      const txnid = response?.data?.data?.txnid;
      if (!redirectUrl) throw new Error('No redirect URL');
      if (txnid) localStorage.setItem('dastaan__pending_txnid', txnid);
        // Mark the payment step in the URL (/new/weekly/payment) while the payment app is open.
      window.history.replaceState(window.history.state, '', '/new/weekly/payment');
      window.location.href = redirectUrl;
    } catch (error) {
      showToast('Could not open payment. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (alreadySubscribed && !checkingPayment) return <Navigate to="/" replace />;

  const phoneValid = phone.length === 10;
  const amount = plan ? plan.plan_price - plan.discount : null;
  const perDay = plan ? Math.round(amount / (Number(plan.plan_days) || 7)) : null;

  const priceBlock = plan && (
    <div className='weekly-page__price'>
      {/* <span className='weekly-page__plan'>{plan.plan_name} Plan</span>
      <span className='weekly-page__price-main'>Just at ₹{perDay}/day</span> */}
      <span className='weekly-page__price-sub'>
        Billed as ₹{amount}/week
        {plan.discount > 0 && (
          <> <s>₹{plan.plan_price}</s> <b>{plan.discount_percent}% off</b></>
        )}
      </span>
    </div>
  );

  return (
    <div
      ref={pageRef}
      className={`weekly-page ${keyboardOpen ? 'is-keyboard-open' : ''}`}
      style={viewport ? { top: viewport.top, height: viewport.height, bottom: 'auto' } : undefined}
    >
      <div className='weekly-page__inner'>
        <div className='weekly-page__header'>
          <img
            className='weekly-page__logo'
            src={`${process.env.PUBLIC_URL}/logo/Dastaan_logo.png`}
            alt="Dastaan"
            title="Dastaan"
          />
        </div>

        <div className='weekly-page__hero'>
          <img src={`${process.env.PUBLIC_URL}/images/hero-image.jpg`} alt="" />
        </div>

        {unconfirmed ? (
          <div className='weekly-page__form'>
            <div className='weekly-page__fail-icon' aria-hidden='true'>!</div>
            <span className='weekly-page__title'>Payment failed</span>
            {/* <span className='weekly-page__subtitle'>
              We couldn't complete your payment. If money was debited, tap Check status — it can take a few minutes to reach us.
            </span> */}
            <button
              type='button'
              className='weekly-page__cta'
              onClick={() => { localStorage.removeItem('dastaan__pending_txnid'); setUnconfirmed(false); setStep('pay'); startPayment(); }}
              disabled={!plan || isSubmitting}
            >
              {isSubmitting ? 'Opening payment...' : 'Try again'}
            </button>
            {/* <button
              type='button'
              className='weekly-page__secondary'
              onClick={() => pollPaymentStatus(localStorage.getItem('dastaan__pending_txnid') || '')}
            >
              Check status
            </button> */}
          </div>
        ) : checkingPayment ? (
          <div className='weekly-page__form'>
            <div className='weekly-page__spinner' />
            <span className='weekly-page__title'>Confirming your payment…</span>
            <span className='weekly-page__subtitle'>Please don't close this page.</span>
            {checkDeadline && <PaymentTimer deadline={checkDeadline} />}
          </div>
        ) : step === 'pay' ? (
          <div className='weekly-page__form'>
            {priceBlock}
            <button
              type='button'
              className='weekly-page__cta'
              onClick={startPayment}
              disabled={!plan || isSubmitting}
            >
              {isSubmitting ? 'Opening payment...' : amount ? `Pay ₹${amount}` : 'Pay Now'}
            </button>
          </div>
        ) : (
          <div className='weekly-page__form'>
            <label className='weekly-page__phone'>
              <span className='weekly-page__cc'>+91</span>
              <input
                ref={phoneRef}
                type='tel'
                inputMode='numeric'
                placeholder='Enter your mobile number'
                maxLength={10}
                value={phone}
                onChange={handlePhoneChange}
                onKeyDown={(e) => { if (e.key === 'Enter' && phoneValid && !isSubmitting) loginWithPhone(); }}
              />
            </label>

            {priceBlock}

            <button
              type='button'
              className='weekly-page__cta'
              onClick={loginWithPhone}
              disabled={!phoneValid || isSubmitting}
            >
              {isSubmitting ? 'Please wait...' : 'Get Started'}
            </button>
          </div>
        )}

        {/* <div className='weekly-page__terms'>
          By Continuing, you agree to Dastaan<br />
          <Link to={'/' + pages.terms}>Terms and Conditions</Link> and <Link to={'/' + pages.privacy}>Privacy Policy</Link>
        </div> */}
      </div>
    </div>
  );
}

export default WeeklyLogin
